import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Clock,
  ListChecks,
  PhoneCall,
  RotateCw,
  Timer
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Sidebar } from '../components/Sidebar'
import {
  buildOperationsReport,
  type OperationsCallStatus,
  type OperationsDashboardCall,
  type OperationsRecoveryItem,
  type OperationsReport
} from '../lib/operationsDashboard'
import { hasInternalApi, internalApiGet } from '../lib/internalApi'
import { retryFailedCall } from '../lib/callRetry'
import { supabase } from '../lib/supabase'
import { useLivers } from '../lib/useLivers'

export function ReportsPage() {
  const qc = useQueryClient()
  const livers = useLivers()
  const calls = useQuery<OperationsDashboardCall[]>({
    queryKey: ['operations-dashboard', 'calls'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<{ calls: OperationsDashboardCall[] }>('/api/calls?limit=1000')
        return data.calls
      }

      const { data, error } = await supabase
        .from('call_records')
        .select('id,liver_id,called_at,duration_seconds,status,todos,next_follow_date,summary,error_message,updated_at,audio_storage_path,transcript')
        .order('called_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as OperationsDashboardCall[]
    }
  })
  const retryCall = useMutation({
    mutationFn: retryRecoveryItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations-dashboard', 'calls'] })
    }
  })
  const report = buildOperationsReport(calls.data ?? [], livers.data ?? [], new Date(), {
    allowStuckRetry: hasInternalApi()
  })
  const loading = calls.isLoading || livers.isLoading
  const error = calls.error ?? livers.error

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">レポート</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                月次の通話量、処理状態、ライバー別の対応量を俯瞰します。
              </p>
            </div>
            {loading && <p className="text-xs text-gray-400">集計中...</p>}
            {error && (
              <p className="text-xs text-red-600">
                レポート取得に失敗: {error instanceof Error ? error.message : String(error)}
              </p>
            )}
          </header>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <ReportMetric
              icon={<PhoneCall className="h-4 w-4" />}
              label="今月の通話"
              value={`${report.monthlyCallCount}件`}
              note={`完了 ${report.monthlyCompleteCallCount} / 失敗 ${report.monthlyFailedCallCount}`}
              tone="sky"
            />
            <ReportMetric
              icon={<CheckCircle2 className="h-4 w-4" />}
              label="完了率"
              value={`${Math.round(report.monthlyCompletionRate * 100)}%`}
              note="今月の complete / 全通話"
              tone="emerald"
            />
            <ReportMetric
              icon={<Timer className="h-4 w-4" />}
              label="平均通話時間"
              value={formatDuration(report.averageDurationSeconds)}
              note={`合計 ${formatDuration(report.monthlyDurationSeconds)}`}
              tone="violet"
            />
            <ReportMetric
              icon={<ListChecks className="h-4 w-4" />}
              label="未完了TODO"
              value={`${report.openTodoCount}件`}
              note={`期限超過 ${report.overdueTodoCount}件`}
              tone={report.overdueTodoCount > 0 ? 'rose' : 'slate'}
            />
          </section>

          <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
            <MonthlyTrendCard report={report} />
            <StatusCard report={report} />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">復旧対象の通話</h2>
                <p className="mt-1 text-xs text-gray-500">
                  failed と stuck 候補を、再処理可否と不可理由つきで確認します。
                </p>
              </div>
              <span className="w-fit rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                {report.recoveryItems.length}件
              </span>
            </div>
            {loading ? (
              <p className="px-4 py-8 text-sm text-gray-400">読み込み中...</p>
            ) : report.recoveryItems.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {report.recoveryItems.map((item) => (
                  <li key={item.id} className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(0,1fr)_130px_160px] md:items-center">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/liver/${item.liverId}`} className="text-sm font-medium text-gray-900 hover:text-sky-700">
                          {item.liverName}
                        </Link>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                          {STATUS_LABEL[item.status]}
                        </span>
                      </div>
                      <p className="mt-1 text-xs leading-5 text-gray-600">{item.reason}</p>
                      {item.errorMessage && <p className="mt-1 line-clamp-1 text-xs text-red-600">{item.errorMessage}</p>}
                    </div>
                    <span className={`w-fit rounded-full px-2 py-1 text-xs font-medium ${item.retryable ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {item.retryable ? `再処理可 -> ${item.targetStatus}` : '自動再処理不可'}
                    </span>
                    <button
                      type="button"
                      disabled={!item.retryable || (retryCall.isPending && retryCall.variables?.callId === item.callId)}
                      onClick={() => retryCall.mutate(item)}
                      className="inline-flex w-fit items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      <RotateCw className={`h-3.5 w-3.5 ${retryCall.isPending && retryCall.variables?.callId === item.callId ? 'animate-spin' : ''}`} />
                      再処理
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-8 text-sm text-gray-400">復旧が必要な通話はありません。</p>
            )}
            {retryCall.error && (
              <p className="border-t border-gray-100 px-4 py-3 text-xs text-red-600">
                再処理に失敗: {retryCall.error instanceof Error ? retryCall.error.message : String(retryCall.error)}
              </p>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">ライバー別 月次サマリー</h2>
                <p className="mt-1 text-xs text-gray-500">
                  通話数、失敗、TODO残数を見て、今月の対応偏りを確認します。
                </p>
              </div>
              <span className="w-fit rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                上位 {report.topLivers.length}件
              </span>
            </div>
            {loading ? (
              <p className="px-4 py-8 text-sm text-gray-400">読み込み中...</p>
            ) : report.topLivers.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {report.topLivers.map((row) => (
                  <li key={row.liverId}>
                    <Link
                      to={`/liver/${row.liverId}`}
                      className="grid gap-3 px-4 py-4 transition hover:bg-gray-50 md:grid-cols-[minmax(0,1fr)_repeat(4,minmax(80px,110px))]"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{row.liverName}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          最終通話 {row.lastCallAt ? formatShortDate(row.lastCallAt) : '-'}
                        </p>
                      </div>
                      <RowStat label="通話" value={`${row.callCount}件`} />
                      <RowStat label="完了" value={`${row.completeCallCount}件`} />
                      <RowStat label="失敗" value={`${row.failedCallCount}件`} danger={row.failedCallCount > 0} />
                      <RowStat label="TODO" value={`${row.openTodoCount}件`} danger={row.overdueTodoCount > 0} />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-8 text-sm text-gray-400">まだレポート対象のデータがありません。</p>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}

function retryRecoveryItem(item: OperationsRecoveryItem): Promise<void> {
  return retryFailedCall({
    id: item.callId,
    status: item.status,
    audio_storage_path: item.audioStoragePath,
    transcript: item.transcript
  })
}

type MetricTone = 'sky' | 'emerald' | 'violet' | 'rose' | 'slate' | 'amber'

function ReportMetric({
  icon,
  label,
  value,
  note,
  tone
}: {
  icon: React.ReactNode
  label: string
  value: string
  note: string
  tone: MetricTone
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}>
          {icon}
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{note}</p>
    </div>
  )
}

function MonthlyTrendCard({ report }: { report: OperationsReport }) {
  const maxCalls = Math.max(1, ...report.monthlyTrend.map((point) => point.callCount))

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">6か月推移</h2>
          <p className="mt-1 text-xs text-gray-500">通話件数と failed の増減を確認します。</p>
        </div>
        <BarChart3 className="h-4 w-4 text-gray-400" />
      </div>
      <div className="space-y-3">
        {report.monthlyTrend.map((point) => (
          <div key={point.month} className="grid grid-cols-[64px_minmax(0,1fr)_48px] items-center gap-3">
            <span className="text-xs text-gray-500">{formatMonth(point.month)}</span>
            <div className="h-3 rounded-full bg-gray-100">
              <div
                className="h-3 rounded-full bg-sky-400"
                style={{ width: `${Math.max(4, (point.callCount / maxCalls) * 100)}%` }}
              />
            </div>
            <span className="text-right text-xs tabular-nums text-gray-600">{point.callCount}件</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function StatusCard({ report }: { report: OperationsReport }) {
  const statuses: OperationsCallStatus[] = [
    'pending',
    'transcribing',
    'awaiting_summary',
    'summarizing',
    'complete',
    'failed'
  ]

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">処理状態</h2>
          <p className="mt-1 text-xs text-gray-500">Gateway / Recorder の滞留確認用です。</p>
        </div>
        <Clock className="h-4 w-4 text-gray-400" />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {statuses.map((status) => (
          <div key={status} className="rounded-lg border border-gray-100 px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500">{STATUS_LABEL[status]}</span>
              <span className={`text-sm font-semibold tabular-nums ${status === 'failed' ? 'text-red-600' : 'text-gray-900'}`}>
                {report.statusCounts[status]}
              </span>
            </div>
          </div>
        ))}
      </div>
      {report.statusCounts.failed > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          failed が残っています。Home またはライバー詳細から再試行してください。
        </div>
      )}
    </section>
  )
}

function RowStat({ label, value, danger = false }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2 md:bg-transparent md:px-0 md:py-0">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold tabular-nums ${danger ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  )
}

const TONE_CLASS: Record<MetricTone, string> = {
  sky: 'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700',
  rose: 'bg-rose-50 text-rose-700',
  slate: 'bg-slate-100 text-slate-700',
  amber: 'bg-amber-50 text-amber-700'
}

const STATUS_LABEL: Record<OperationsCallStatus, string> = {
  pending: 'pending',
  recording: 'recording',
  transcribing: 'transcribing',
  awaiting_summary: 'awaiting_summary',
  summarizing: 'summarizing',
  complete: 'complete',
  failed: 'failed'
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0分'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}分`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours}時間` : `${hours}時間${rest}分`
}

function formatShortDate(value: string): string {
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric'
  })
}

function formatMonth(month: string): string {
  const [, value] = month.split('-')
  return `${Number(value)}月`
}
